import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { post } from '../../services/ApiService';
import { resetBmiValues } from '../../redux/slices/BmiSlice';
import { deleteAllFromCart } from '../../redux/slices/CartSlice';
import { resetConsultation } from '../../redux/slices/ConsultationSlice';
import { resetVaccineConsultation } from '../../redux/slices/vaccineConsultationSlice';
import VaccineAssessmentModal from '../TravelClinic/VaccineAssessmentModal';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

const PaymentComponent = ({formData, selectedDate, selectedTimeSlot, cartItems,total, validateCheckoutForm, istravelClinic, form, questions, setIsLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState(total || 0);
  const [errors, setErrors] = useState({});
  const user = useSelector((state) => state.user.user);
  const bmi = useSelector((state) => state.bmi);
  const consultation = useSelector((state) => state.consultation);
  const vaccineConsultation = useSelector(state => state.vaccineConsultation);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const redirectUrl = `${process.env.REACT_APP_BASE_URL}/appointment/confirmation`;
  const [showConsultation, setShowConsultation] = useState(false);
  const handleClose = () => setShowConsultation(false);
  const handleShow = () => setShowConsultation(true);

  useEffect(() => {
    if (istravelClinic) {
      setShowPaymentForm(vaccineConsultation.answers.length > 0 && vaccineConsultation.formId);
    } else {
      setShowPaymentForm(true);
    }
  }, [istravelClinic, vaccineConsultation]);

  const createPaymentMethod = async (cardElement, stripe) => {
    try {
      if (!cardElement) {
        throw new Error("Card not found.");
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${formData?.firstname} ${formData?.lastname}`,
          email: formData?.email,
          phone: formData?.phone,
        }
      });
    if (error) throw error;
    if (!paymentMethod) throw new Error("Failed to create payment");
      return paymentMethod;
    } catch (error) {
      throw error;
    }
  };

const createPaymentIntent = async (pmId, amount) => {
  try {

    let productsData = JSON.stringify(cartItems.map(item => ({
        name: item.name,
        price: item?.price,
        quantity: 1,
    })));

    if (productsData.length > 500) {
      productsData = productsData.substring(0, 497) + "...";
    }
    const body = {
      pmId: pmId,
      amount: amount * 100, 
      metadata: {
        customer_name: `${formData?.firstname} ${formData?.lastname}`,
        customer_email: formData?.email,
        customer_phone: formData?.phone,
        products: productsData,
      },
      description: `Payment for Booking by ${formData?.email}`,
    };
    
    const res = await post(`api/booking/create-payment-intent`, body);
    
    if (!res.success) {
      throw new Error(res.message || "Failed to create payment intent");
    }
    
    return { 
      clientSecret: res.clientSecret, 
      status: res.status,
      paymentIntentId: res.id 
    };  
  } catch (error) {
    throw error; // Don't handle here, let the caller handle
  }
};

const confirmPayment = async (clientSecret, cardElement, stripe) => {

  try {
    const return_url = `${window.location.origin}/checkout`;
    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      { 
        payment_method: { card: cardElement }
      }
    );
    
    if (error) throw error;
    return paymentIntent;
  } catch (error) {
    throw error;
  }
};

const handleCardAction = async (clientSecret, stripe) => {
  try {
    const { error, paymentIntent } = await stripe.handleCardAction(clientSecret);
    if (error) throw error;
    return paymentIntent;
  } catch (error) {
    throw error;
  }
};

    
// Enhanced error handler
const handleError = (error) => {
  console.error('Payment error:', error);
  
  // Handle Stripe-specific errors
  if (error.type === 'StripeCardError' || error.code) {
    switch (error.code) {
      case "incomplete_number":
        setErrors({ cardNumber: error.message });
        break;
      case "incomplete_expiry":
        setErrors({ expiry: error.message });
        break;
      case "incomplete_cvc":
        setErrors({ cvc: "CVC is incomplete" });
        break;
      case "card_declined":
        setErrors({ general: "Your card was declined. Please try another payment method." });
        break;
      case "expired_card":
        setErrors({ general: "Your card has expired. Please use a different card." });
        break;
      case "card_expired":
        setErrors({ general: "Your card has expired. Please use a different card." });
        break;
      case "processing_error":
        setErrors({ general: "Payment processing failed. Please try again." });
        break;
      default:
        setErrors({ general: error.message });
    }
  } 
  // Handle network/API errors
  else if (error.message.includes("Network Error") || error.message.includes("Failed to fetch")) {
    setErrors({ general: "Network error. Please check your connection and try again." });
  }
  // Handle server errors
  else if (error.response?.status >= 500) {
    setErrors({ general: "Server error. Please try again later." });
  }
  // Default case
  else {
    setErrors({ general: error.message || "An unexpected error occurred. Please try again." });
  }
};

const handlePayment = async (event) => {

  event.preventDefault();

  if (!validateCheckoutForm()) {
    return;
  }

  setErrors({});
  setLoading(true);

  if (!stripe || !elements) {
    setErrors({ general: "Payment system not initialized. Please refresh the page." });
    setLoading(false);
    return;
  }

  const cardElement = elements.getElement(CardNumberElement);

  try {
    // Step 1: Create payment method
    const paymentMethod = await createPaymentMethod(cardElement, stripe);


    // Step 2: Create payment intent
    const { clientSecret, status, paymentIntentId } = await createPaymentIntent(paymentMethod.id, amount);
    
    let paymentIntent;
    
    // Step 3: Handle different payment intent statuses
    switch (status) {
      case 'requires_confirmation':
        paymentIntent = await confirmPayment(clientSecret, cardElement, stripe);
        
        if (paymentIntent?.status === "requires_action") {
          paymentIntent = await handleCardAction(clientSecret, stripe);
        }
        
        if (paymentIntent?.status === 'succeeded') {
          await createOrder(paymentIntent.id);
          return; // Success case - redirect or show success message
        }
        
        if (paymentIntent?.status === 'canceled') {
          throw new Error("Payment was canceled.");
        }
        break;
        
      case 'requires_payment_method':
        throw new Error("Payment method required. Please try again.");
        
      case 'succeeded':
        await createOrder(paymentIntentId);
        return; // Success case
        
      case 'canceled':
        throw new Error("Payment was canceled.");
        
      default:
        throw new Error(`Unhandled payment status: ${status}`);
    }
    
  } catch (error) {
    handleError(error);
  } finally {
    setLoading(false);
  }
};

  const createOrder = async(paymentIntentId) => {
    try {
        const body = {
            userId:user?._id,
            bookingDate:selectedDate,
            bookingTime: selectedTimeSlot,
            products: cartItems,
            consultation: consultation,
            paymentIntentId,
            billingAddress: formData,
            total:amount,
            bmi:{
              height:bmi?.heightCm,
              weight:bmi?.weightKg,
              score:bmi?.bmiValue,
              category:bmi?.bmiCategory
            },
            vaccineConsultation: vaccineConsultation
            
        }
        const res = await post(`api/booking/create`, body);
        if(res.success){
            clearSession(dispatch);
            navigate(`/booking/confirmation/${res.bookingRef}`);
        }else{
            throw new Error("Failed to Charge payment, please try again"); 
        }
    } catch (error) {
      handleError(error);
      return null;
    }
  }

  const clearSession = (dispatch) => {
    dispatch(resetBmiValues());  
    dispatch(deleteAllFromCart()); 
    dispatch(resetConsultation());
    dispatch(resetVaccineConsultation()); 
  };

  const handleCardNumberChange = (event) => {
    if (event.error) {
      setErrors((prev) => ({ ...prev, cardNumber: event.error.message }));
    } else {
      setErrors((prev) => ({ ...prev, cardNumber: null }));
    }
  };
  const handleExpiryChange = (event) => {
    if (event.error) {
      setErrors((prev) => ({ ...prev, expiry: event.error.message }));
    } else {
      setErrors((prev) => ({ ...prev, expiry: null }));
    }
  };
  const handleCvcChange = (event) => {
    if (event.error) {
      setErrors((prev) => ({ ...prev, cvc: event.error.message }));
    } else {
      setErrors((prev) => ({ ...prev, cvc: null }));
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "18px",
        top:'10px',
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
    classes: {
      base: "input_text",
    },
    showIcon: true,
    supportedNetworks: ['visa', 'mastercard', 'amex', 'discover'],
  };


  return (
    <>
    {showPaymentForm ? (
    <>
{istravelClinic && vaccineConsultation.answers.length > 0 && (
  <div 
    className="col-12 mt-8" 
    style={{
      background: '#efeded', // A smooth gradient background
      color: 'black',
      padding: '20px',
      textAlign: 'center',
      transition: 'all 0.3s ease', // Smooth transition for hover effect
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <h6 className="" style={{margin: 0 }}>
        Travel vaccination assessment completed!
      </h6>
    </div>
  </div>
)}


    <div className="d-flex mt-8 text-center mb-5 card-payment-img" style={{ marginTop: '30px !important' }}>
      <div className="col-auto me-2">
        <img className="w-60" src="/assets/images/payments/card1.png" alt='' />
      </div>
      <div className="col-auto me-2">
        <img className="w-70" src="/assets/images/payments/card2.png" alt='' />
      </div>
      <div className="col-auto me-2">
        <img className="w-70" src="/assets/images/payments/card3.png" alt='' />
      </div>
      <div className="col-auto me-2">
        <img className="w-70" src="/assets/images/payments/card4.png" alt='' />
      </div>
      <div className="col-auto me-2">
        <img className="w-70" src="/assets/images/payments/card5.png" alt='' />
      </div>
    </div>
    <form onSubmit={handlePayment}>
      <div id="payment-element" className='mt-4'>
        <div className='row'>
          <div className='col-12'>
            <div className="form-group">
              <label className="fw-normal">
                Card Number <span class="text-danger">*</span>
              </label>
              <CardNumberElement
                options={cardElementOptions}
                onChange={handleCardNumberChange}
              />
              {/* {errors.cardNumber && (
                <div className="error-message">
                  <p className="error-text">
                    {errors.cardNumber}
                  </p>
                </div>
              )} */}
            </div>
          </div>
          <div className='col-8 mt-4'>
            <div className="form-group">
              <label className="fw-normal">
                Expiry <span class="text-danger">*</span>
              </label>
              <CardExpiryElement
                options={cardElementOptions}
                onChange={handleExpiryChange}
              />{" "}
              {/* {errors.expiry && (
                <div className="error-message">
                  <p className="error-text">
                    {errors.expiry}
                  </p>{" "}
                </div>
              )} */}
            </div>
          </div>
          <div className='col-4 mt-4'>
            <div className="form-group">
              <label className="fw-normal">
                CVC <span class="text-danger">*</span>
              </label>
              <CardCvcElement
                options={cardElementOptions}
                onChange={handleCvcChange}
              />{" "}

            </div>
          </div>
        </div>
        <div className='col-12 me-2'>
          {errors.cardNumber && <div className="error-message">{errors.cardNumber}</div>}
          {errors.expiry && <div className="error-message">{errors.expiry}</div>}
          {errors.cvc && <div className="error-message">{errors.cvc}</div>}
          {errors.general && <div className="error-message">{errors.general}</div>}
        </div>
        <button type="submit" className="btn-show w-100 metropill"  disabled={!stripe || loading}>
          {loading ? 'Processing...' : 'PAY'}
        </button>
      </div>
    </form>
    </>
    ):(
      <>
        <h5 className='text-center mt-6 text-danger'>Travel vaccine assessment required!</h5>
        <p className='text-center' style={{fontSize:'13px'}}>Please complete your travel vaccine assessment to proceed with your purchase.</p>
        <button type="button" className="btn-checkout-show btn-show mt-0 w-100 metropill" onClick={() => (handleShow())}>
          Click here to Start Vaccine Assessment
        </button>
      </>
    )}

    <VaccineAssessmentModal
      showConsultation={showConsultation}
      handleClose={handleClose}
      form = {form}
      questions = {questions}
      setIsLoading={setIsLoading}
    />
    </>
  );
};

export default PaymentComponent;
